import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

import Icon from "../../components/AppIcon";
import ProductCrudTable from "./components/ProductCrudTable";
import ProductFormModal from "./components/ProductFormModal";
import ProductViewModal from "./components/ProductViewModal";
import DeleteConfirmModal from "../users-crud-management/components/DeleteConfirmModal";

import { useDispatch, useSelector } from "react-redux";
import {
  createProductService,
  deleteProductService,
  getAllProductsService,
  updateProductService,
} from "redux/services/products_sevice";
import { getAllgovernoratesService } from "redux/services/general_service";
import { getTagsService } from "redux/services/workshops_service";
import { toast } from "react-toastify";

const DiscountProductCrudManagment = () => {
  const dispatch = useDispatch();
  const { productList, metaProduct } = useSelector((state) => state.products);

  const [language, setLanguage] = useState("en");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formMode, setFormMode] = useState("add");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language") || "en";
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    dispatch(getAllProductsService({ page: 1, "is-discount": 1 }));
    dispatch(getAllgovernoratesService({ page: 1 }));
    dispatch(getTagsService({ page: 1 }));
  }, [dispatch]);

  const labels = {
    title: { en: "Products Management", ar: "إدارة المنتجات" },
    addNew: { en: "Add New Product", ar: "إضافة منتج جديد" },
    search: { en: "Search products...", ar: "البحث عن المنتجات..." },
    noResults: { en: "No products found", ar: "لم يتم العثور على منتجات" },
  };

  const handleAddProduct = () => {
    setFormMode("add");
    setSelectedProduct(null);
    setIsFormModalOpen(true);
  };

  const handleEditProduct = (product) => {
    setFormMode("edit");
    setSelectedProduct(product);
    setIsFormModalOpen(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsViewModalOpen(true);
  };

  const handleDeleteProduct = (product) => {
    setSelectedProduct(product);
    setIsDeleteModalOpen(true);
  };

  const handleSaveProduct = (productData) => {
    if (formMode === "add") {
      dispatch(createProductService(productData)).then((response) => {
        if (
          response?.payload?.status === 200 ||
          response?.payload?.status === 201
        ) {
          dispatch(getAllProductsService({ page: 1 }));
          toast("تمت عملية الإضافة بنجاح !");
          setIsFormModalOpen(false);
        } else {
          Object.values(response.payload.response?.data?.errors)
            .flatMap((errorArray) => errorArray)
            .forEach((error) => toast.warn(error));
        }
      });
    } else {
      dispatch(
        updateProductService({
          id: selectedProduct?.id,
          formData: productData,
        }),
      ).then((response) => {
        if (
          response?.payload?.status === 200 ||
          response?.payload?.status === 201
        ) {
          dispatch(getAllProductsService({ page: 1 }));
          setIsFormModalOpen(false);
        } else {
          Object.values(response.payload.response?.data?.errors)
            .flatMap((errorArray) => errorArray)
            .forEach((error) => toast.warn(error));
        }
      });
    }
  };

  const handleConfirmDelete = () => {
    dispatch(deleteProductService(selectedProduct?.id)).then((response) => {
      if (response.payload.status === 200 || response.payload.status === 204) {
        dispatch(getAllProductsService({ page: 1 }));
        toast("تم الحذف بنجاح ");
        setIsDeleteModalOpen(false);
        setSelectedProduct(null);
      }
    });
  };

  const filteredProducts = productList?.filter(
    (product) =>
      product?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      product?.description
        ?.toLowerCase()
        .includes(searchQuery?.toLowerCase()) ||
      product?.tags?.some((tag) =>
        tag.name.toLowerCase().includes(searchQuery?.toLowerCase()),
      ),
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {labels.title[language]}
          </h1>
        </div>

        <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={labels.search[language]}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  dispatch(
                    getAllProductsService({
                      search: e.target.value,
                      "is-discount": 1,
                    }),
                  );
                  setPage(1);
                }}
                className="w-full"
              />
            </div>
            <Button
              onClick={handleAddProduct}
              iconName="Plus"
              variant="default"
            >
              {labels.addNew[language]}
            </Button>
          </div>

          {filteredProducts?.length === 0 ? (
            <div className="text-center py-12">
              <Icon
                name="Package"
                size={48}
                className="mx-auto text-muted-foreground mb-4"
              />
              <p className="text-text-secondary">
                {labels.noResults[language]}
              </p>
            </div>
          ) : (
            <ProductCrudTable
              products={productList}
              metaProduct={metaProduct}
              language={language}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onView={handleViewProduct}
            />
          )}
        </div>
      </div>

      {isFormModalOpen && (
        <ProductFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          onSave={handleSaveProduct}
          product={selectedProduct}
          mode={formMode}
          language={language}
        />
      )}
      {isViewModalOpen && (
        <ProductViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          product={selectedProduct}
          language={language}
          onEdit={() => {
            setIsViewModalOpen(false);
            handleEditProduct(selectedProduct);
          }}
          onDelete={() => {
            setIsViewModalOpen(false);
            handleDeleteProduct(selectedProduct);
          }}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
          itemName={selectedProduct?.name}
          language={language}
        />
      )}
    </div>
  );
};

export default DiscountProductCrudManagment;
